import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './layout/AppShell';
import { RequireAuth } from './routes/RequireAuth';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { UsersPage } from './pages/UsersPage';
import { RolesPage } from './pages/RolesPage';
import { AuditPage } from './pages/AuditPage';
import { SettingsPage } from './pages/SettingsPage';
import { MenuPage } from './pages/MenuPage';
import { RecipesPage } from './pages/RecipesPage';
import { InventoryPage } from './pages/InventoryPage';
import { PurchasePage } from './pages/PurchasePage';
import { POSPage } from './pages/POSPage';
import { OrdersPage } from './pages/OrdersPage';
import { KdsPage } from './pages/KdsPage';
import { BillingPage } from './pages/BillingPage';
import { ReportsPage } from './pages/ReportsPage';
import { CrmPage } from './pages/CrmPage';
import { ReservationsPage } from './pages/ReservationsPage';
import { StaffPage } from './pages/StaffPage';
import { ExpensesPage } from './pages/ExpensesPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { Toasts } from './components/ui/Toasts';
import { useAuth } from './store/auth';
import { useUi } from './store/ui';

export function App() {
  const restore = useAuth((s) => s.restore);
  const status = useAuth((s) => s.status);

  // On first paint, try to resume the session from the refresh cookie.
  useEffect(() => { void restore(); }, [restore]);

  // Keyboard shortcuts (POS-friendly, and required by the brief).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ctrl/Cmd+K -> command palette, Alt+D -> toggle theme.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        useUi.getState().setCommandOpen(true);
      }
      if (e.altKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        const ui = useUi.getState();
        ui.setTheme(ui.theme === 'dark' ? 'light' : 'dark');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const location = useLocation();

  if (status === 'loading') return <BootSplash />;

  return (
    <>
      <Routes>
        <Route path="/login" element={status === 'authenticated' ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            <Route index element={<DashboardPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* Screens that arrive in later phases - gated by permission today. */}
            <Route path="pos" element={<POSPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="kds" element={<KdsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="menu" element={<MenuPage />} />
            <Route path="recipes" element={<RecipesPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="purchase" element={<PurchasePage />} />
            <Route path="crm" element={<CrmPage />} />
            <Route path="reservations" element={<ReservationsPage />} />
            <Route path="staff" element={<StaffPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="reports" element={<ReportsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Toasts />
      {/* Announce route changes to screen readers. */}
      <span className="sr-only" aria-live="polite">{location.pathname}</span>
    </>
  );
}

function BootSplash() {
  return (
    <div className="flex h-full items-center justify-center bg-surface-0">
      <div className="flex flex-col items-center gap-3">
        <span className="h-9 w-9 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <p className="text-sm text-ink-muted">Starting NoshOS POS…</p>
      </div>
    </div>
  );
}
